var _a, _b;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { times } from 'lodash';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { onEnterOrSpacePress } from '@hivebrite/utils';
import { Box } from "../../box/src";
import { Button } from "../../button/src";
import { CATEGORIES, FontAwesomeIcon } from "../../font-awesome-icon/src";
import { focusOutline } from "../../theme/src";
const ariaLabelPrevPage = ((_a = window === null || window === void 0 ? void 0 : window.s__) === null || _a === void 0 ? void 0 : _a.call(window, 'Accessibility|Previous page')) || 'Previous page';
const ariaLabelNextPage = ((_b = window === null || window === void 0 ? void 0 : window.s__) === null || _b === void 0 ? void 0 : _b.call(window, 'Accessibility|Next page')) || 'Next page';
export const Pagination = ({ currentPage, itemsPerPage, onPageChange, totalItems, }) => {
    const pagesCount = useMemo(() => Math.ceil(totalItems / itemsPerPage), [totalItems, itemsPerPage]);
    const firstPageIsSelected = currentPage === 1;
    const lastPageIsSelected = currentPage === pagesCount;
    const handlePrevious = useCallback(() => {
        if (currentPage === 1) {
            onPageChange(1);
        }
        else {
            onPageChange(currentPage - 1);
        }
    }, [currentPage, onPageChange]);
    const handleNext = useCallback(() => {
        if (currentPage === pagesCount) {
            onPageChange(currentPage);
        }
        else {
            onPageChange(currentPage + 1);
        }
    }, [currentPage, pagesCount, onPageChange]);
    const simpleDisplay = useMemo(() => (_jsx(_Fragment, { children: times(pagesCount, (pageIndex) => {
            const pageNumber = pageIndex + 1;
            const isSelected = pageNumber === currentPage;
            return (_jsx(Page, { isSelected: isSelected, onPageChange: onPageChange, page: pageNumber }, pageNumber));
        }) })), [currentPage, pagesCount, onPageChange]);
    const complexDisplay = useMemo(() => {
        const secondPageIsSelected = currentPage === 2;
        const thirdPageIsSelected = currentPage === 3;
        const lastPage = pagesCount;
        const antepenultimatePageIsSelected = currentPage === lastPage - 2;
        const penultimatePageIsSelected = currentPage === lastPage - 1;
        const displayPreviousPage = !(firstPageIsSelected || secondPageIsSelected);
        const displayPreviousDots = !(firstPageIsSelected ||
            secondPageIsSelected ||
            thirdPageIsSelected);
        const displayCurrentPage = !(firstPageIsSelected || lastPageIsSelected);
        const displayNextDots = !(antepenultimatePageIsSelected ||
            penultimatePageIsSelected ||
            lastPageIsSelected);
        const displayNextPage = !(penultimatePageIsSelected || lastPageIsSelected);
        return (_jsxs(_Fragment, { children: [_jsx(Page, { isSelected: firstPageIsSelected, onPageChange: onPageChange, page: 1 }), displayPreviousDots && _jsx(Dots, {}), displayPreviousPage && (_jsx(Page, { isSelected: false, onPageChange: onPageChange, page: currentPage - 1 })), displayCurrentPage && (_jsx(Page, { isSelected: true, onPageChange: onPageChange, page: currentPage })), displayNextPage && (_jsx(Page, { isSelected: false, onPageChange: onPageChange, page: currentPage + 1 })), displayNextDots && _jsx(Dots, {}), _jsx(Page, { isSelected: lastPageIsSelected, onPageChange: onPageChange, page: lastPage })] }));
    }, [
        currentPage,
        pagesCount,
        onPageChange,
        firstPageIsSelected,
        lastPageIsSelected,
    ]);
    return (_jsxs(Box, { "data-testid": "pagination", flexWrap: "wrap", justifyContent: "center", mt: -3, children: [_jsx(Button, { "aria-label": ariaLabelPrevPage, icon: () => (_jsx(FontAwesomeIcon, { height: "16px", iconName: "angle-left", iconType: CATEGORIES.LIGHT })), isDisabled: firstPageIsSelected, mr: 2, mt: 3, onClick: handlePrevious, size: "small", variant: "inversed" }), pagesCount < 7 ? simpleDisplay : complexDisplay, _jsx(Button, { "aria-label": ariaLabelNextPage, icon: () => (_jsx(FontAwesomeIcon, { height: "16px", iconName: "angle-right", iconType: CATEGORIES.LIGHT })), isDisabled: lastPageIsSelected, ml: 2, mt: 3, onClick: handleNext, size: "small", variant: "inversed" })] }));
};
const Dots = () => (_jsx(Box, { alignItems: "center", font: "body-semibold-asphalt", height: 30, justifyContent: "center", mt: 3, mx: 1, pb: "6px", width: 30, children: "..." }));
const Page = ({ isSelected, onPageChange, page }) => {
    var _a;
    return (_jsx(PageWrapper, { "aria-current": isSelected, "aria-label": ((_a = window === null || window === void 0 ? void 0 : window.s__) === null || _a === void 0 ? void 0 : _a.call(window, 'Accessibility|Page %{page}').i__({ page })) ||
            `Page ${page}`, "data-testid": isSelected ? 'page-selected' : undefined, isSelected: isSelected, onClick: () => onPageChange(page), onKeyPress: onEnterOrSpacePress(() => onPageChange(page)), children: page }));
};
const PageWrapper = styled(Box)
    .withConfig({
    shouldForwardProp: (prop) => !['isSelected'].includes(prop),
})
    .attrs(({ isSelected }) => ({
    alignItems: 'center',
    as: 'span',
    color: isSelected && 'primary',
    font: 'body-semibold-asphalt',
    height: 30,
    justifyContent: 'center',
    minWidth: 30,
    mt: 3,
    mx: 1,
    px: 1,
    role: 'button',
    tabIndex: 0,
})) `
   ${focusOutline}

   cursor: pointer;
   border: ${(props) => props.isSelected ? `1px solid ${props.theme.colors.primary}` : 'inherit'};
   border-radius: 5px;
   user-select: none;

   &:hover {
     color: ${(props) => !props.isSelected && props.theme.colors.primary};
   }
 `;
//# sourceMappingURL=Pagination.js.map